<script setup>
import { computed, h } from 'vue';
import {
  useVueTable,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
} from '@tanstack/vue-table';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';

import Skeleton from 'dashboard/components-next/skeleton/Skeleton.vue';
import EmptyState from 'dashboard/components-next/empty-state/EmptyState.vue';
import Table from 'dashboard/components/table/Table.vue';
import Pagination from 'dashboard/components/table/Pagination.vue';

const { teams, teamMetrics } = defineProps({
  teams: {
    type: Array,
    default: () => [],
  },
  teamMetrics: {
    type: Array,
    default: () => [],
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const { uiSettings, updateUISettings } = useUISettings();

// UI Settings key for team table page size
const TEAM_TABLE_PAGE_SIZE_KEY = 'report_overview_team_table_page_size';

// Get the saved page size from UI settings or default to 10
const getPageSize = () => {
  return uiSettings.value[TEAM_TABLE_PAGE_SIZE_KEY] || 10;
};

const handlePageSizeChange = pageSize => {
  updateUISettings({ [TEAM_TABLE_PAGE_SIZE_KEY]: pageSize });
};

const getTeamMetrics = id =>
  teamMetrics.find(metrics => metrics.team_id === Number(id)) || {};

const tableData = computed(() =>
  teams
    .map(team => {
      const metric = getTeamMetrics(team.id);
      return {
        agent: team.name,
        open: metric.open || 0,
        unattended: metric.unattended || 0,
      };
    })
    .sort((a, b) => {
      // First sort by open tickets (descending)
      const openDiff = b.open - a.open;
      // If open tickets are equal, sort by name (ascending)
      if (openDiff === 0) {
        return a.agent.localeCompare(b.agent);
      }
      return openDiff;
    })
);

// Numeric cells use tabular-nums for stable digit width during realtime updates.
const defaulSpanRender = cellProps =>
  h(
    'span',
    {
      class: cellProps.getValue()
        ? 'capitalize text-n-slate-12 tabular-nums font-medium'
        : 'text-n-slate-10 tabular-nums',
    },
    cellProps.getValue() ? cellProps.getValue() : '—'
  );

const columnHelper = createColumnHelper();
const columns = [
  columnHelper.accessor('agent', {
    header: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.TEAM'),
    cell: defaulSpanRender,
    size: 250,
  }),
  columnHelper.accessor('open', {
    header: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.OPEN'),
    cell: defaulSpanRender,
    size: 100,
  }),
  columnHelper.accessor('unattended', {
    header: t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.TABLE_HEADER.UNATTENDED'),
    cell: defaulSpanRender,
    size: 100,
  }),
];

const table = useVueTable({
  get data() {
    return tableData.value;
  },
  columns,
  enableSorting: false,
  getCoreRowModel: getCoreRowModel(),
  getPaginationRowModel: getPaginationRowModel(),
  initialState: {
    pagination: {
      pageSize: getPageSize(),
    },
  },
});
</script>

<template>
  <div class="flex flex-col flex-1">
    <div
      v-if="isLoading"
      class="flex flex-col gap-3 p-4"
      role="status"
      aria-busy="true"
      :aria-label="$t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.LOADING_MESSAGE')"
    >
      <Skeleton v-for="i in 6" :key="i" variant="table-row" />
    </div>

    <EmptyState
      v-else-if="!teams.length"
      icon="i-lucide-users-round"
      :title="$t('OVERVIEW_REPORTS.TEAM_CONVERSATIONS.NO_TEAMS')"
      variant="compact"
    />

    <template v-else>
      <Table :table="table" class="max-h-[calc(100vh-21.875rem)]" />
      <Pagination
        class="mt-2"
        :table="table"
        show-page-size-selector
        :default-page-size="getPageSize()"
        @page-size-change="handlePageSizeChange"
      />
    </template>
  </div>
</template>
